import { action, computed } from 'mobx';

import { ViewController } from '@estee/elc-service';

import {
    OTP_ERROR_MESSAGE_TOO_MANY_ATTEMPTS_TRANSLATION_KEY,
    OTP_TYPE_EMAIL
} from '~constants/Constants';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { UserRepository } from '~repositories/UserRepository';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';

interface IEmailFormViewController {
    userRepository: UserRepository;
    configRepository: ConfigRepository;
}
export class EmailFormViewController extends ViewController {
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;
    private messageRepository: MessageRepository;

    constructor({ userRepository, configRepository }: IEmailFormViewController) {
        super();

        this.userRepository = userRepository;
        this.configRepository = configRepository;
        this.messageRepository = diContainer.get(serviceNames.messageRepository);
    }

    @computed
    public get data() {
        const {
            resetView,
            signInWithOtpFormStore,
            signInWithOtpFormStore: {
                values: { emailAddress }
            }
        } = this.userRepository;
        const { fieldsValidations, emailIdentifierFieldName } = this.configRepository;

        return {
            errorMessages: this.messageRepository.messages.get(MessageGroup.EMAIL),
            formStore: signInWithOtpFormStore,
            sendToText: emailAddress,
            emailValidations: fieldsValidations[emailIdentifierFieldName as string],
            resetView
        };
    }

    @action
    public load = () => {
        const {
            accountSignInFormStore: {
                values: { accountIdentifier }
            }
        } = this.userRepository;

        this.messageRepository.clearAllMessages();
        this.userRepository.signInWithOtpFormStore.setEmailAddress(accountIdentifier);
    };

    @action
    public sendEmailButtonClicked = async () => {
        const {
            signInWithOtpFormStore: {
                values: { emailAddress },
                validateForm,
                isValid,
                setSubmitting
            }
        } = this.userRepository;
        const { clearMessagesByGroup } = this.messageRepository;

        clearMessagesByGroup(MessageGroup.EMAIL);
        validateForm();

        if (isValid) {
            const request = {
                emailAddress
            };
            setSubmitting(true);
            const success = await this.userRepository.generateCode(request);

            if (success) {
                this.userRepository.otpStore.setData({
                    showOtp: true,
                    type: OTP_TYPE_EMAIL
                });

                this.userRepository.resetView();
            } else {
                const transKey = this.userRepository.otpStore.data.translationKey;

                if (transKey === OTP_ERROR_MESSAGE_TOO_MANY_ATTEMPTS_TRANSLATION_KEY) {
                    this.messageRepository.setMessages(MessageGroup.SIGN_IN, [{ transKey }]);
                    this.userRepository.resetView();
                } else {
                    this.messageRepository.setMessages(MessageGroup.EMAIL, [{ transKey }]);
                }
            }
            setSubmitting(false);
        }
    };
}
